import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStripe, useElements, CardElement, IbanElement } from "@stripe/react-stripe-js";
import toast, { Toaster } from "react-hot-toast";
import Logo from "../../../common/assets/blue-logo.svg";
import {
  fetchActivationInfo,
  payActivation,
  confirm3ds,
  almaCheckout,
  ActivationInfo,
  PaymentMode,
} from "../services/activationService";

const STRIPE_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#12141d",
      fontFamily: '"Outfit", system-ui, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "15px",
      fontWeight: "400",
      "::placeholder": { color: "#94a3b8" },
    },
    invalid: { color: "#DC2626", iconColor: "#DC2626" },
  },
};

const CARD_OPTIONS = { ...STRIPE_ELEMENT_OPTIONS, hidePostalCode: true };
const IBAN_OPTIONS = {
  ...STRIPE_ELEMENT_OPTIONS,
  supportedCountries: ["SEPA"],
  placeholderCountry: "FR",
};

const formatEuros = (cents: number) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

const formatDate = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Activation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [info, setInfo] = useState<ActivationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedMode, setSelectedMode] = useState<PaymentMode>("comptant_cb");

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [mandateAccepted, setMandateAccepted] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [ibanComplete, setIbanComplete] = useState(false);
  const [showMandateDetails, setShowMandateDetails] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError("Lien invalide");
      setLoading(false);
      return;
    }
    fetchActivationInfo(token)
      .then(setInfo)
      .catch((err) => {
        const msg = err.response?.data?.message || "Lien invalide ou expiré";
        setLoadError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-[#5e6075] text-sm">Chargement…</div>
      </div>
    );
  }

  if (loadError || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-10 max-w-md w-full text-center">
          <h1 className="text-2xl font-semibold text-[#12141d] mb-3 tracking-tight">
            Lien non valide
          </h1>
          <p className="text-[#5e6075] text-sm mb-6">
            {loadError || "Ce lien d'activation n'est plus utilisable."}
          </p>
          <a
            href="mailto:contact@fideliz.fr"
            className="text-[#0068FE] text-sm hover:underline"
          >
            contact@fideliz.fr
          </a>
        </div>
      </div>
    );
  }

  const mode = info.modes[selectedMode];
  const isMonthly = selectedMode === "monthly";
  const isAlma = selectedMode === "comptant_alma";

  const canSubmit = (() => {
    if (!info || submitting) return false;
    if (!cgvAccepted) return false;
    if (password.length < 6 || password !== passwordConfirm) return false;
    if (isAlma) return true;
    if (!stripe || !elements || !cardComplete) return false;
    if (isMonthly && (!ibanComplete || !mandateAccepted)) return false;
    return true;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!info || !token) return;
    if (password !== passwordConfirm) {
      setSubmitError("Les mots de passe ne correspondent pas");
      return;
    }
    if (password.length < 6) {
      setSubmitError("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    if (!cgvAccepted) {
      setSubmitError("Vous devez accepter les CGV pour continuer");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      if (isAlma) {
        const { url } = await almaCheckout(token, { password });
        if (!url) throw new Error("URL Alma manquante");
        window.location.href = url;
        return;
      }

      if (!stripe || !elements) throw new Error("Stripe non chargé");
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Formulaire non chargé");

      const cardPm = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name: `${info.firstName} ${info.lastName}`, email: info.email },
      });
      if (cardPm.error) throw new Error(cardPm.error.message || "CB refusée");

      let sepaPmId: string | undefined;
      if (isMonthly) {
        const ibanElement = elements.getElement(IbanElement);
        if (!ibanElement) throw new Error("IBAN non chargé");
        const sepaPm = await stripe.createPaymentMethod({
          type: "sepa_debit",
          sepa_debit: ibanElement,
          billing_details: { name: `${info.firstName} ${info.lastName}`, email: info.email },
        });
        if (sepaPm.error) throw new Error(sepaPm.error.message || "IBAN invalide");
        sepaPmId = sepaPm.paymentMethod.id;
      }

      let result = await payActivation(token, {
        paymentMode: selectedMode === "comptant_cb" ? "comptant_cb" : "monthly",
        cardPaymentMethodId: cardPm.paymentMethod.id,
        ibanPaymentMethodId: sepaPmId,
        password,
      });

      if (result.requires_action && result.payment_intent_client_secret) {
        const confirm = await stripe.confirmCardPayment(result.payment_intent_client_secret);
        if (confirm.error) {
          throw new Error(confirm.error.message || "Authentification échouée");
        }
        result = await confirm3ds(token, {
          paymentIntentId: result.payment_intent_id!,
          ibanPaymentMethodId: sepaPmId,
          password,
        });
      }

      if (result.success) {
        toast.success("Compte activé !");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        throw new Error(result.message || "Erreur lors de l'activation");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Erreur inconnue";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Outfit',system-ui,sans-serif]">
      <Toaster position="top-center" />

      <header className="border-b border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-6 py-5">
          <img src={Logo} alt="Fideliz" className="h-7 w-auto" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <div className="mb-10">
          <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#0068FE] mb-3">
            Mise en service
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#12141d] tracking-tight leading-tight mb-3">
            Lançons votre application
          </h1>
          <p className="text-base text-[#5e6075] leading-relaxed">
            Bonjour <span className="text-[#12141d] font-medium">{info.firstName}</span>,
            règlement des frais de mise en service pour démarrer le développement et la
            configuration de l'application{" "}
            <span className="text-[#12141d] font-medium">{info.opticianName}</span>.
          </p>
        </div>

        {/* Choix du mode */}
        <div className="mb-6">
          <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#5e6075] mb-3">
            Choisissez votre mode de paiement
          </div>
          <div className="grid grid-cols-1 gap-3">
            {/* Comptant CB — recommandé */}
            <ModeCard
              selected={selectedMode === "comptant_cb"}
              recommended
              onClick={() => setSelectedMode("comptant_cb")}
              title="Comptant par CB"
              subtitle="Payez tout en une fois et économisez 3 %"
              priceLabel={`${formatEuros(info.modes.comptant_cb.todayTtcCents)} € TTC`}
              priceHint={`au lieu de ${formatEuros(info.modes.monthly.totalTtcCents)} €`}
              savings={`Économisez ${formatEuros(info.comptantSavingsTtcCents)} € TTC`}
            />
            {/* Mensuel */}
            <ModeCard
              selected={selectedMode === "monthly"}
              onClick={() => setSelectedMode("monthly")}
              title="Mensuel"
              subtitle={`${formatEuros(info.monthlyTtcCents)} € TTC / mois pendant ${info.engagementMonths} mois`}
              priceLabel={`${formatEuros(info.modes.monthly.todayTtcCents)} € TTC aujourd'hui`}
              priceHint="puis 1er de chaque mois (SEPA)"
            />
          </div>

          {/* Alma — option discrète, masquée temporairement
              (en attente d'activation côté Stripe / Alma).
              Pour réactiver : retirer le `false &&` ci-dessous. */}
          {false && (
            <button
              type="button"
              onClick={() => setSelectedMode("comptant_alma")}
              className={`mt-3 w-full text-left px-4 py-2.5 rounded-lg border transition-colors flex items-center justify-between gap-3 ${
                selectedMode === "comptant_alma"
                  ? "border-[#0068FE] bg-[#EEF9FF]"
                  : "border-[#E5E7EB] bg-white hover:border-[#94a3b8]"
              }`}
            >
              <span className="text-sm text-[#5e6075]">
                ou en{" "}
                <span className="text-[#12141d] font-medium">3 fois sans frais</span> avec
                Alma —{" "}
                <span className="text-[#12141d] font-medium">
                  3 × {formatEuros(Math.round(info.modes.comptant_alma.todayTtcCents / 3))} € TTC
                </span>
              </span>
              <span className="text-xs text-[#0068FE] whitespace-nowrap">
                {selectedMode === "comptant_alma" ? "Sélectionné" : "Choisir →"}
              </span>
            </button>
          )}
        </div>

        {/* Échéancier */}
        <div className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-medium tracking-[0.16em] uppercase text-[#5e6075]">
              {isMonthly ? "Échéancier" : "Détail du paiement"}
            </div>
            <div className="text-xs text-[#5e6075]">
              Engagement {info.engagementMonths + info.monthsOffered} mois
              {info.monthsOffered > 0
                ? ` (dont ${info.monthsOffered} offert${info.monthsOffered > 1 ? "s" : ""})`
                : ""}
            </div>
          </div>

          {/* Lignes du schedule */}
          <div className="space-y-1.5 text-sm mb-4">
            {mode.schedule.map((s, i) => (
              <div key={i} className="flex justify-between items-center gap-3 py-1">
                <span className={s.offered ? "text-[#5e6075]" : "text-[#12141d]"}>
                  {isMonthly && (
                    <>
                      <span className="tabular-nums">{formatDate(s.date)}</span>
                      <span className="mx-2 text-[#94a3b8]">·</span>
                    </>
                  )}
                  {s.label}
                  {!s.offered && isMonthly && (
                    <span className="ml-2 text-[#5e6075] text-xs">
                      ({s.method === "card" ? "CB" : "SEPA"})
                    </span>
                  )}
                </span>
                <span className="font-medium whitespace-nowrap">
                  {s.offered ? (
                    <span className="text-[#16A34A] font-semibold text-xs uppercase tracking-wider">
                      Offert
                    </span>
                  ) : (
                    <span className="text-[#12141d]">{formatEuros(s.ht)} € HT</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* Récap HT / TVA / TTC */}
          <div className="pt-4 border-t border-[#E5E7EB] space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-[#5e6075]">Sous-total HT</span>
              <span className="text-[#12141d] font-medium">
                {formatEuros(mode.totalHtCents)} €
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5e6075]">TVA {(info.vatRate * 100).toFixed(0)} %</span>
              <span className="text-[#12141d] font-medium">
                {formatEuros(mode.totalTtcCents - mode.totalHtCents)} €
              </span>
            </div>
            <div className="flex justify-between pt-2 mt-1 border-t border-[#E5E7EB]">
              <span className="text-[#12141d] font-semibold">Total TTC</span>
              <span className="text-[#12141d] font-bold text-lg tracking-tight">
                {formatEuros(mode.totalTtcCents)} €
              </span>
            </div>
          </div>

          {/* Pour Alma : répartition en 3 */}
          {isAlma && (
            <div className="mt-3 pt-3 border-t border-[#E5E7EB] text-xs text-[#5e6075]">
              Réparti en{" "}
              <span className="text-[#12141d] font-medium">
                3 × {formatEuros(Math.round(mode.totalTtcCents / 3))} € TTC
              </span>{" "}
              prélevés par Alma.
            </div>
          )}

          {/* Mention "à régler aujourd'hui" pour mensuel (où le total ≠ aujourd'hui) */}
          {isMonthly && (
            <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex justify-between text-sm">
              <span className="text-[#5e6075]">À régler aujourd'hui (CB)</span>
              <span className="text-[#12141d] font-semibold">
                {formatEuros(mode.todayTtcCents)} € TTC
              </span>
            </div>
          )}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-[#f6f6f9] border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-6"
        >
          {/* CB (cachée pour Alma) */}
          {!isAlma && (
            <div>
              <label className="block text-sm font-medium text-[#12141d] mb-2">
                Carte bancaire
              </label>
              <p className="text-xs text-[#5e6075] mb-3">
                {isMonthly
                  ? "Pour le paiement d'aujourd'hui"
                  : `Pour le paiement comptant (${formatEuros(mode.todayTtcCents)} € TTC)`}
              </p>
              <div className="px-4 py-3.5 border border-[#E5E7EB] rounded-lg bg-white focus-within:border-[#12141d] transition-colors">
                <CardElement options={CARD_OPTIONS} onChange={(e) => setCardComplete(e.complete)} />
              </div>
            </div>
          )}

          {/* IBAN — uniquement en mode mensuel */}
          {isMonthly && (
            <>
              <div>
                <label className="block text-sm font-medium text-[#12141d] mb-2">IBAN</label>
                <p className="text-xs text-[#5e6075] mb-3">
                  Pour les {info.engagementMonths} prélèvements SEPA mensuels (1er de chaque mois)
                </p>
                <div className="px-4 py-3.5 border border-[#E5E7EB] rounded-lg bg-white focus-within:border-[#12141d] transition-colors">
                  <IbanElement
                    options={IBAN_OPTIONS}
                    onChange={(e) => setIbanComplete(e.complete)}
                  />
                </div>
              </div>

              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mandateAccepted}
                    onChange={(e) => setMandateAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#0068FE] cursor-pointer"
                  />
                  <span className="text-sm text-[#12141d] leading-relaxed">
                    J'autorise Fideliz à prélever mon compte par SEPA pour les mensualités
                    à venir.
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowMandateDetails((v) => !v)}
                  className="ml-7 mt-1 text-xs text-[#5e6075] hover:text-[#12141d] underline"
                >
                  {showMandateDetails ? "Masquer les détails" : "Voir les détails du mandat"}
                </button>
                {showMandateDetails && (
                  <p className="ml-7 mt-2 text-xs text-[#5e6075] leading-relaxed">
                    En signant ce mandat, vous autorisez Fideliz à envoyer des instructions
                    à votre banque pour débiter votre compte, et votre banque à débiter
                    votre compte conformément aux instructions de Fideliz. Vous bénéficiez
                    d'un droit à remboursement par votre banque selon les conditions
                    décrites dans la convention signée avec elle. Toute demande de
                    remboursement doit être présentée dans les 8 semaines suivant la date
                    de débit.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Mention Alma */}
          {isAlma && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-sm text-[#5e6075]">
              Vous serez redirigé vers Alma pour finaliser le paiement en 3 fois sans
              frais. Aucune CB à saisir ici.
            </div>
          )}

          {/* CGV */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={cgvAccepted}
              onChange={(e) => setCgvAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#0068FE] cursor-pointer"
            />
            <span className="text-sm text-[#12141d] leading-relaxed">
              J'accepte les{" "}
              <a
                href="https://fideliz.fr/cgv"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0068FE] hover:underline"
              >
                conditions générales de vente
              </a>{" "}
              de Fideliz.
            </span>
          </label>

          {/* Password */}
          <div className="pt-6 border-t border-[#E5E7EB]">
            <label className="block text-sm font-medium text-[#12141d] mb-2">
              Choisir un mot de passe
            </label>
            <p className="text-xs text-[#5e6075] mb-3">
              Vous l'utiliserez pour vous connecter à votre espace.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="6 caractères minimum"
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg bg-white focus:border-[#12141d] focus:outline-none transition-colors text-sm text-[#12141d] placeholder-[#94a3b8]"
              />
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Confirmer"
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg bg-white focus:border-[#12141d] focus:outline-none transition-colors text-sm text-[#12141d] placeholder-[#94a3b8]"
              />
            </div>
          </div>

          {submitError && (
            <div className="bg-[#fef2f2] border border-[#fecaca] text-[#DC2626] rounded-lg p-3 text-sm">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-4 bg-[#0068FE] text-white rounded-lg font-medium text-[15px] hover:bg-[#0052CC] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting
              ? isAlma
                ? "Redirection vers Alma…"
                : "Paiement en cours…"
              : isAlma
              ? "Payer avec Alma →"
              : `Démarrer mon application — ${formatEuros(mode.todayTtcCents)} € TTC`}
          </button>

          <p className="text-xs text-[#5e6075] text-center">
            Paiement sécurisé par Stripe. Vos données bancaires ne transitent pas par nos
            serveurs.
          </p>
        </form>
      </main>

      <footer className="max-w-2xl mx-auto px-6 pb-10">
        <p className="text-xs text-[#94a3b8] text-center">
          Une question ?{" "}
          <a href="mailto:contact@fideliz.fr" className="text-[#0068FE] hover:underline">
            contact@fideliz.fr
          </a>
        </p>
      </footer>
    </div>
  );
};

interface ModeCardProps {
  selected: boolean;
  recommended?: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
  priceLabel: string;
  priceHint?: string;
  savings?: string;
}

const ModeCard: React.FC<ModeCardProps> = ({
  selected,
  recommended,
  onClick,
  title,
  subtitle,
  priceLabel,
  priceHint,
  savings,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative text-left p-5 rounded-2xl border-2 transition-all ${
      selected
        ? "border-[#0068FE] bg-[#EEF9FF]"
        : "border-[#E5E7EB] bg-white hover:border-[#94a3b8]"
    }`}
  >
    {recommended && (
      <span className="absolute -top-2 right-4 bg-[#0068FE] text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full">
        Recommandé
      </span>
    )}
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="font-semibold text-[#12141d]">{title}</div>
        <div className="text-xs text-[#5e6075] mt-0.5">{subtitle}</div>
        {savings && (
          <div className="inline-block mt-2 text-xs font-medium text-[#16A34A] bg-[#dcfce7] px-2 py-0.5 rounded">
            {savings}
          </div>
        )}
      </div>
      <div className="text-right whitespace-nowrap">
        <div className="font-bold text-[#12141d]">{priceLabel}</div>
        {priceHint && <div className="text-xs text-[#5e6075] mt-0.5">{priceHint}</div>}
      </div>
    </div>
  </button>
);

export default Activation;
